import { useState } from 'react';
import { Button, Menu, MenuItem, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { Link } from 'react-router';

export type BasicMenuItem = {
  label: string;
  onClick?: () => void;
  url?: string;
  icon?: React.ReactNode;
  testId?: string;
};

export type BasicMenuProps = {
  items: BasicMenuItem[];
  title: string;
  variant?: 'dark';
  disabled?: boolean;
  styles?: {
    button?: string;
    menu?: string;
    menuItem?: string;
  };
  testId?: string;
};

const toMenuItem = (
  item: BasicMenuItem,
  onItemClick: (callback?: () => void) => void,
) => {
  if (item?.url) {
    return (
      <MenuItem
        data-testid={item.testId}
        key={item.label}
        className="min-w-[150px] p-0"
      >
        <Link to={item.url} className="px-4 py-1.5 w-full">
          <Typography className="text-black flex gap-2">
            {item.icon}
            {item.label}
          </Typography>
        </Link>
      </MenuItem>
    );
  }

  return (
    <MenuItem
      data-testid={item.testId}
      key={item.label}
      onClick={() => onItemClick(item.onClick)}
      className="min-w-[150px]"
    >
      {item.icon ? (
        <Typography className="text-black flex gap-2">
          {item.icon}
          {item.label}
        </Typography>
      ) : (
        item.label
      )}
    </MenuItem>
  );
};

export const BasicMenu = ({
  items,
  title,
  variant,
  disabled,
  testId,
}: BasicMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl((state) => (state === null ? event.currentTarget : null));
  };

  const onItemClick = (callback?: () => void) => {
    setAnchorEl(null);
    callback && callback();
  };

  return (
    <>
      <Button
        data-testid={testId}
        onClick={handleClick}
        variant="contained"
        className="px-3"
        disabled={disabled}
        sx={
          variant === 'dark'
            ? {
                backgroundColor: 'primary.dark',
                '&:hover': {
                  backgroundColor: '#2C3548',
                },
              }
            : undefined
        }
      >
        <div className="flex gap-2">
          {title}
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            className="self-center"
          />
        </div>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClick}
        className="mt-3"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {items.map((item) => toMenuItem(item, onItemClick))}
      </Menu>
    </>
  );
};
